import {REMOTE_SENSING_DATE_STATES} from './remoteSensingDateModel'
import {isPlanetHdDateAllowed} from './planetHdAcquisitionQuery'

const STATUS_ORDER = [
  REMOTE_SENSING_DATE_STATES.READY,
  REMOTE_SENSING_DATE_STATES.REJECTED_QUALITY,
  REMOTE_SENSING_DATE_STATES.CANDIDATE,
  REMOTE_SENSING_DATE_STATES.PREPARING,
  REMOTE_SENSING_DATE_STATES.REJECTED,
  REMOTE_SENSING_DATE_STATES.FAILED,
]

const compareText = (left, right) => (left < right ? -1 : left > right ? 1 : 0)

const isCalendarDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}

export const planetHdItemKey = (item) =>
  item?.acquisitionId || item?.itemId || item?.resultId || ''

export const hasKnownCloudPercent = (item) =>
  Number.isFinite(item?.cloudPercent) && item.cloudPercent >= 0 && item.cloudPercent <= 100

export const isPlanetHdClearReady = (item, threshold) =>
  isPlanetHdDateAllowed(item?.date) &&
  item?.status === REMOTE_SENSING_DATE_STATES.READY &&
  hasKnownCloudPercent(item) &&
  Number.isFinite(threshold) && threshold >= 0 && threshold <= 100 &&
  item.cloudPercent <= threshold

// Explicit navigation may request an image that has not been cached yet.
export const isPlanetHdNavigable = (item, threshold) =>
  isPlanetHdDateAllowed(item?.date) &&
  [REMOTE_SENSING_DATE_STATES.READY, REMOTE_SENSING_DATE_STATES.CANDIDATE,
    REMOTE_SENSING_DATE_STATES.PREPARING].includes(item?.status) &&
  hasKnownCloudPercent(item) && Number.isFinite(threshold) && threshold >= 0 && threshold <= 100 &&
  item.cloudPercent <= threshold

const statusOrder = (item) => {
  const index = STATUS_ORDER.indexOf(item.status)
  return index < 0 ? STATUS_ORDER.length : index
}

const compareDayItems = (left, right) => {
  const leftCoverage = Number.isFinite(left?.coveragePercent) ? left.coveragePercent : 100
  const rightCoverage = Number.isFinite(right?.coveragePercent) ? right.coveragePercent : 100
  if (leftCoverage !== rightCoverage) return leftCoverage > rightCoverage ? -1 : 1
  const leftValid = Number.isFinite(left?.validPercent)
    ? left.validPercent
    : Number.isFinite(left?.clearPercent) ? left.clearPercent
      : hasKnownCloudPercent(left) ? 100 - left.cloudPercent : 0
  const rightValid = Number.isFinite(right?.validPercent)
    ? right.validPercent
    : Number.isFinite(right?.clearPercent) ? right.clearPercent
      : hasKnownCloudPercent(right) ? 100 - right.cloudPercent : 0
  if (leftValid !== rightValid) return leftValid > rightValid ? -1 : 1
  const leftCloud = hasKnownCloudPercent(left) ? left.cloudPercent : Infinity
  const rightCloud = hasKnownCloudPercent(right) ? right.cloudPercent : Infinity
  if (leftCloud !== rightCloud) return leftCloud < rightCloud ? -1 : 1
  const statusDifference = statusOrder(left) - statusOrder(right)
  if (statusDifference) return statusDifference
  // Use code-point ordering, independent of the browser's locale and API order.
  for (const getKey of [planetHdItemKey, (item) => item.itemId || '', (item) => item.resultId || '',
    (item) => item.status || '']) {
    const difference = compareText(String(getKey(left)), String(getKey(right)))
    if (difference) return difference
  }
  return 0
}

export const selectBestPlanetHdDayItem = (dayItems) => {
  const eligible = Array.isArray(dayItems)
    ? dayItems.filter((item) => isPlanetHdDateAllowed(item?.date)) : []
  if (!eligible.length) return null
  const active = eligible.filter((item) => [
    REMOTE_SENSING_DATE_STATES.READY,
    REMOTE_SENSING_DATE_STATES.CANDIDATE,
    REMOTE_SENSING_DATE_STATES.PREPARING,
  ].includes(item?.status))
  return [...(active.length ? active : eligible)].sort(compareDayItems)[0] || null
}

export const selectBestPlanetHdItemsByDate = (items) => {
  const groups = new Map()
  for (const item of Array.isArray(items) ? items : []) {
    if (!isCalendarDate(item?.date) || !isPlanetHdDateAllowed(item.date)) continue
    const dayItems = groups.get(item.date) || []
    dayItems.push(item)
    groups.set(item.date, dayItems)
  }
  return Array.from(groups.values()).map(selectBestPlanetHdDayItem).filter(Boolean)
}

// Day color describes cloud coverage, never readiness. The representative is
// chosen from the group responsible for that color so its label/click agree.
export const getPlanetHdDayCloudState = (dayItems, threshold) => {
  const representative = selectBestPlanetHdDayItem(dayItems)
  const items = representative ? [representative] : []
  if (!Number.isFinite(threshold) || threshold < 0 || threshold > 100) {
    return {cloudState: 'unknown', representative: items[0] || null}
  }
  const knownItems = items.filter(hasKnownCloudPercent)
  const clear = knownItems.find((item) => item.cloudPercent <= threshold)
  return clear ? {cloudState: 'clear', representative: clear}
    : knownItems.length ? {cloudState: 'cloudy', representative: knownItems[0]}
      : {cloudState: 'unknown', representative: items[0] || null}
}

// Pure view model: no requests, state updates or preparation side effects.
export const buildPlanetHdCalendarModel = (items, cloudinessFilter) => {
  const groups = new Map()
  for (const item of selectBestPlanetHdItemsByDate(items)) {
    if (!isCalendarDate(item?.date) || !isPlanetHdDateAllowed(item.date)) continue
    const dayItems = groups.get(item.date) || []
    dayItems.push(item)
    groups.set(item.date, dayItems)
  }
  const availableDates = Array.from(groups.keys()).sort((left, right) => compareText(right, left))
  const itemsByDate = new Map(availableDates.map((date) => [date, groups.get(date).sort(compareDayItems)]))
  const navigableDates = availableDates.filter((date) =>
    itemsByDate.get(date).some((item) => isPlanetHdNavigable(item, cloudinessFilter))
  )
  return {itemsByDate, availableDates, navigableDates}
}

export const selectPlanetHdDayItem = (dayItems, selectedAcquisitionId) => {
  return selectBestPlanetHdDayItem(dayItems)
}

export const getPlanetHdAdjacentDates = (navigableDates, inspectedDate) => {
  if (!isCalendarDate(inspectedDate)) return {previousDate: null, nextDate: null}
  const dates = Array.from(new Set(Array.isArray(navigableDates) ? navigableDates : []))
    .filter((date) => isCalendarDate(date) && isPlanetHdDateAllowed(date)).sort(compareText)
  return {
    previousDate: [...dates].reverse().find((date) => date < inspectedDate) || null,
    nextDate: dates.find((date) => date > inspectedDate) || null,
  }
}
